import { observer } from 'mobx-react-lite'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { Box, Button, Icon, type IDropdownItem, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { formatPhoneNumberNational, logger } from 'shared/lib'
import { IntegrationsRoutes, SettingsRoutes } from 'entities/ExportRouters'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { PhoneApi } from 'entities/Phone'
import type { IResponseIntegrationNumber } from 'entities/Phone/api/types'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { RejectAssignedNumbersAlert } from 'pages/settings/ui/RejectAssignedNumbersAlert/RejectAssignedNumbersAlert'
import styles from './styles.module.scss'
import { type SelectAppAndNumbersStore } from './store/SelectAppAndNumbersStore'

type ISelectAppAndNumbersCommonProps = {
  store: SelectAppAndNumbersStore
  nextClickHandler: () => void
  customButtons?: (
    nextClickHandler: () => void,
    isDisabled?: boolean,
    loading?: boolean
  ) => ReactNode
}

export const SelectAppAndNumbersCommon = observer(
  ({ store, nextClickHandler, customButtons }: ISelectAppAndNumbersCommonProps) => {
    const {
      integrationList,
      selectedIntegration,
      selectedIntegrationKey,
      setSelectedIntegrationKey,
      activeNumbersList,
      inactiveNumbersList,
      isNumbersLoading,
      selectedNumber,
      selectedNumberId,
      setSelectedNumberId,
      setNumberSearch,
      numberSearch,
      isIntegrationSelected,
      isNextDisabled,
      selectedIntegrationSupportLink,
    } = store
    const { integrationsStore } = useEntitiesContext()
    const { loading: integrationsLoading } = integrationsStore

    const integrationDropdownItems = useMemo<IDropdownItem[]>(() => {
      return integrationList.map((integration) => {
        let iconRight: 'alertTriangleYellow' | 'plug' | undefined
        let labelRight: ReactNode | undefined
        if (integration.isConnected) {
          iconRight = undefined
          labelRight = undefined
        } else if (integration.shouldReconnect) {
          iconRight = 'alertTriangleYellow'
          labelRight = 'Reconnect'
        } else {
          iconRight = 'plug'
          labelRight = 'Connect'
        }

        return {
          id: integration.key,
          value: integration.key,
          data: integration,
          iconL: integration.icon,
          label: integration.name,
          noRenderEmptyActiveValue: true,
          labelRight: !integration.isConnected ? (
            <div className={styles.rightLabelContainer}>
              <Typography ariaLabel={'Status'} variant='body-md-regular' color={'tertiary'}>
                {labelRight}
              </Typography>
              <Icon icon={iconRight} tertiary />
            </div>
          ) : undefined,
          onClick: () => {
            if (!integration.isConnected) {
              uiStore.changeRoute({
                path: `/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${integration.key}`,
              })
            }
          },
        }
      })
    }, [integrationList, selectedIntegrationKey])

    const activeNumberDropdownItems = useMemo<IDropdownItem[]>(() => {
      const icon = selectedIntegration?.icon
      const id = String(selectedNumberId)

      const items = activeNumbersList.map((number) => ({
        id: number.id,
        label: formatPhoneNumberNational(number.formatted_number),
        value: String(number.id),
        data: number,
        iconL: icon,
        noRenderEmptyActiveValue: true,
        labelRight: (
          <Typography ariaLabel={'Owner Name'} variant='body-md-regular' color={'tertiary'}>
            {number.users[0]?.full_name}
          </Typography>
        ),
      }))

      const selectedIndex = items.findIndex((item) => item.value === id)
      if (selectedIndex > 0) {
        const [selected] = items.splice(selectedIndex, 1)
        items.unshift(selected)
      }

      return items
    }, [activeNumbersList, selectedIntegrationKey, selectedNumberId])

    const inactiveNumberDropdownItems = useMemo<IDropdownItem[]>(() => {
      return inactiveNumbersList.map((number) => ({
        id: number.id,
        label: formatPhoneNumberNational(number.formatted_number),
        value: String(number.id),
        data: number,
        iconL: selectedIntegration?.icon,
        disabled: true,
        noRenderEmptyActiveValue: true,
        labelRight: selectedIntegrationSupportLink ? (
          <Button
            tag='a'
            contained='tertiary'
            size='small'
            intent='default'
            text={`Contact ${selectedIntegration?.name}`}
            target='_blank'
            rel='noreferrer'
            href={selectedIntegrationSupportLink}
            className={styles.helpButton}
          />
        ) : undefined,
      }))
    }, [inactiveNumbersList, selectedIntegrationKey])

    const notFoundText = useMemo(() => {
      const hasItems = activeNumbersList.length > 0 || inactiveNumbersList.length > 0
      const isSearch = numberSearch.trim().length > 0

      return !hasItems && !isSearch ? 'No numbers' : undefined
    }, [activeNumbersList, inactiveNumbersList, numberSearch])

    const [loadingNext, setLoadingNext] = useState(false)

    const [rejectNumberByAircall, setRejectNumberByAircall] =
      useState<IResponseIntegrationNumber | null>(null)

    const handleNext = async () => {
      setLoadingNext(true)
      try {
        if (selectedIntegration?.key === 'aircall' && selectedNumber) {
          await PhoneApi.checkAircallNumber(selectedNumber.id)
        }
        nextClickHandler()
      } catch (err) {
        if (selectedNumber) {
          setRejectNumberByAircall(selectedNumber)
        }

        logger.error(err)
      } finally {
        setLoadingNext(false)
      }
    }

    useEffect(() => {
      if (rejectNumberByAircall) {
        if (rejectNumberByAircall.id !== selectedNumber?.id) {
          setRejectNumberByAircall(null)
        }
      }
    }, [rejectNumberByAircall, selectedNumber])

    return (
      <>
        <SelectField
          labelText='App'
          value={selectedIntegrationKey}
          setValue={setSelectedIntegrationKey}
          items={integrationDropdownItems}
          withSearch={false}
          loading={integrationsLoading}
          btnProps={{
            size: 'large',
            className: styles.select,
            text: selectedIntegration ? (
              <>
                <Icon icon={selectedIntegration.icon} />
                <Typography ariaLabel='name' variant='body-md-regular'>
                  {selectedIntegration.name}
                </Typography>
              </>
            ) : (
              'Select'
            ),
          }}
          className={styles.integrationSelect}
        />

        {isIntegrationSelected && (
          <SelectField
            labelText='Phone number'
            value={selectedNumberId}
            setValue={setSelectedNumberId}
            items={[...activeNumberDropdownItems, ...inactiveNumberDropdownItems]}
            loading={isNumbersLoading}
            dropdownProps={{
              onChangeOpen: (isOpen) => {
                if (isOpen) {
                  setNumberSearch('')
                }
              },
              propsDropdownList: {
                textFieldProps: {
                  value: numberSearch,
                  onChange: setNumberSearch,
                },
                notFoundText,
              },
            }}
            btnProps={{
              size: 'large',
              className: styles.select,
              text: selectedNumber ? (
                <>
                  <Icon icon={selectedIntegration?.icon} />
                  <Typography ariaLabel='name' variant='text-input-field-r'>
                    {formatPhoneNumberNational(selectedNumber.formatted_number)}
                  </Typography>
                  {selectedNumber.users[0]?.full_name && (
                    <Typography
                      variant={'body-md-regular'}
                      color={'var(--content-neutral-primary)'}
                      ariaLabel={'dot'}
                    >
                      •
                    </Typography>
                  )}
                  <Typography variant={'text-input-field-r'} color='tertiary' ariaLabel='OwnerName'>
                    {selectedNumber.users[0]?.full_name}
                  </Typography>
                </>
              ) : (
                'Select'
              ),
            }}
          />
        )}

        {rejectNumberByAircall && (
          <>
            <Box height={32} />
            <RejectAssignedNumbersAlert
              rejectAssignedNumbers={[rejectNumberByAircall]}
              clearRejectAssignedNumbers={() => setRejectNumberByAircall(null)}
              airCallLink={selectedIntegrationSupportLink || ''}
            />
          </>
        )}

        {customButtons
          ? customButtons(handleNext, isNextDisabled || !!rejectNumberByAircall, loadingNext)
          : isIntegrationSelected && (
              <NextButton
                disabled={isNextDisabled || !!rejectNumberByAircall}
                text='Next'
                onClick={handleNext}
                loading={loadingNext}
              />
            )}
      </>
    )
  }
)
